/**
 * AWIN Click ID (awc) capture and cart propagation utility.
 *
 * Responsibilities:
 *  - Read `awc` from the URL query string on page load
 *  - Store/refresh it in a cookie named `awc` for 7 days
 *  - Sync the value into Shopify cart attributes as `_awc`
 *  - Idempotent: skips the cart update request if the attribute already matches
 *  - Fails gracefully: no broken cart UX if any request fails
 */

window.AwinCart = (function () {
  var COOKIE_NAME = 'awc';
  var CART_ATTRIBUTE_KEY = '_awc';
  var COOKIE_DAYS = 7;

  /**
   * Read a single query parameter from the current page URL.
   * @param {string} name
   * @returns {string|null}
   */
  function getParam(name) {
    try {
      var params = new URLSearchParams(window.location.search);
      return params.get(name);
    } catch (e) {
      return null;
    }
  }

  /**
   * Read a cookie value by name.
   * @param {string} name
   * @returns {string|null}
   */
  function getCookie(name) {
    try {
      var match = document.cookie.match(
        new RegExp('(?:^|;\\s*)' + name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '=([^;]*)')
      );
      return match ? decodeURIComponent(match[1]) : null;
    } catch (e) {
      return null;
    }
  }

  /**
   * Write a cookie.
   * @param {string} name
   * @param {string} value
   * @param {number} days
   */
  function setCookie(name, value, days) {
    try {
      var expires = new Date();
      expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
      document.cookie =
        name +
        '=' +
        encodeURIComponent(value) +
        '; expires=' +
        expires.toUTCString() +
        '; path=/; SameSite=Lax';
    } catch (e) {
      // fail silently
    }
  }

  /**
   * Capture awc from the URL and store/refresh the cookie if present.
   */
  function captureFromUrl() {
    var awc = getParam(COOKIE_NAME);
    if (awc) {
      setCookie(COOKIE_NAME, awc, COOKIE_DAYS);
    }
  }

  /**
   * Sync the stored awc cookie value into Shopify cart attributes.
   * Idempotent: fetches current cart first and skips the update if already in sync.
   * Fails gracefully on any network or parse error.
   */
  function syncToCart() {
    try {
      var awc = getCookie(COOKIE_NAME);
      if (!awc) {
        // No awc stored — nothing to do
        return;
      }

      // Fetch current cart to check existing attribute value
      fetch('/cart.js', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      })
        .then(function (response) {
          if (!response.ok) {
            throw new Error('cart.js fetch failed: ' + response.status);
          }
          return response.json();
        })
        .then(function (cart) {
          var currentValue =
            cart.attributes && cart.attributes[CART_ATTRIBUTE_KEY]
              ? cart.attributes[CART_ATTRIBUTE_KEY]
              : null;

          // Idempotency check — skip if already in sync
          if (currentValue === awc) {
            return;
          }

          // Write the awc value into cart attributes
          var attributes = {};
          attributes[CART_ATTRIBUTE_KEY] = awc;

          return fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ attributes: attributes }),
          });
        })
        .then(function (response) {
          // response may be undefined if we returned early from idempotency check
          if (response && !response.ok) {
            throw new Error('cart/update.js failed: ' + response.status);
          }
        })
        .catch(function (e) {
          // Fail silently — never break cart UX
          console.warn('[AwinCart] syncToCart error:', e);
        });
    } catch (e) {
      // Outer safety net
      console.warn('[AwinCart] syncToCart unexpected error:', e);
    }
  }

  /**
   * Initialise on page load:
   *  1. Capture awc from URL if present
   *  2. Sync to cart attributes
   */
  function init() {
    captureFromUrl();
    syncToCart();
  }

  // Run on DOMContentLoaded (awin.js is loaded with defer so DOM is ready,
  // but guard with the event in case it fires before defer completes)
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose public API for use by cart.js hooks
  return {
    getParam: getParam,
    getCookie: getCookie,
    setCookie: setCookie,
    syncToCart: syncToCart,
  };
})();
